import { useParams, useLocation, useNavigate, Navigate } from 'react-router';
import { useSuspenseQuery } from '@apollo/client/react';

import { Main } from 'src/components/Layout';
import { usePageMeta } from 'src/hooks';
import { IntegrationDocument } from 'src/graphql/actions/integration/integration.query';
import { MembersTable } from 'src/containers/IntegrationMembers';
import {
  DisconnectDialog,
  IntegrationContextProvider,
  IntegrationSetupDialog,
  ReconnectOauthDialog,
  UpdateCustomIntegrationDialog,
} from 'src/containers/Integrations';

import { Header, SharefileAccessAlert } from './ui';

const IntegrationPage = () => {
  const { id: integrationId = '' } = useParams();
  const { state, pathname } = useLocation();
  const navigate = useNavigate();

  const {
    data: { integration },
  } = useSuspenseQuery(IntegrationDocument, {
    variables: {
      integrationId,
    },
  });
  usePageMeta(integration.accountName ?? 'Integrations');

  if (!integration.policy.read) return <Navigate to="/404" replace />;

  const breadcrumbItems = [
    {
      label: 'Integrations',
      to: '/integrations',
    },
    {
      label: integration.accountName ?? '',
    },
  ];

  const onShareDialogClose = () => navigate(pathname, { replace: true, state: null });

  return (
    <IntegrationContextProvider>
      <Main
        breadcrumbItems={breadcrumbItems}
        className="bg-background-secondary"
        headerChildren={<Header id={integration.id} type={integration.__typename!} />}
      >
        <div className="container mx-auto space-y-4">
          <div className="space-y-1">
            <div className="text-base font-semibold text-foreground">Users with access</div>
            <p className="text-sm text-gray-500">
              Users with access can use this integration to add assets to their products.
            </p>
          </div>
          <MembersTable
            integrationId={integrationId}
            canManage={integration.policy.createMember}
            type={integration.__typename!}
            autoOpenShare={state?.openShareAccess}
            onShareDialogClose={onShareDialogClose}
          />
          {integration.__typename === 'ShareFileIntegration' && <SharefileAccessAlert />}
        </div>
      </Main>
      <IntegrationSetupDialog />
      <DisconnectDialog />
      <UpdateCustomIntegrationDialog />
      <ReconnectOauthDialog />
    </IntegrationContextProvider>
  );
};

export default IntegrationPage;
